import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { ArrowLeft, ArrowUpDown, ClipboardList, Download, Loader2, Search } from 'lucide-react';
import ReportDownloadLog from '@/components/ReportDownloadLog';
import { DateRangeFilter } from '@/components/po-grn/DateRangeFilter';
import { CsvDownloadButton } from '@/components/CsvDownloadButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 50;

const fmtINR = (n) =>
  Number.isFinite(Number(n))
    ? Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '—';
const fmtQty = (n) =>
  Number.isFinite(Number(n)) ? Number(n).toLocaleString('en-IN', { maximumFractionDigits: 3 }) : '0';

const KPI = ({ label, value, accent, testid }) => (
  <Card className={`border-2 ${accent}`} data-testid={testid}>
    <CardContent className="p-4">
      <div className="text-[11px] uppercase tracking-wide text-slate-500 font-medium leading-tight">{label}</div>
      <div className="text-xl font-bold text-slate-900 mt-1">{value}</div>
    </CardContent>
  </Card>
);

const COLUMNS = [
  { key: 'order_number', label: 'Order #' },
  { key: 'order_date', label: 'Date' },
  { key: 'order_status', label: 'Status' },
  { key: 'fulfilment_status', label: 'Fulfilment' },
  { key: 'payment_status', label: 'Payment' },
  { key: 'franchise_name', label: 'Franchisee' },
  { key: 'store_location', label: 'Location' },
  { key: 'order_type', label: 'Order Type' },
  { key: 'product_name', label: 'Product' },
  { key: 'sku', label: 'SKU' },
  { key: 'barcode', label: 'Barcode' },
  { key: 'ordered_mrp', label: 'Ordered MRP', num: true },
  { key: 'ordered_qty', label: 'Qty', num: true, qty: true },
  { key: 'allocated_qty', label: 'Allocated', num: true, qty: true },
  { key: 'pending_qty', label: 'Pending', num: true, qty: true },
  { key: 'line_status', label: 'Line Status' },
  { key: 'linked_invoice', label: 'Invoice' },
  { key: 'linked_dc', label: 'DC' },
];

const FILTER_DEFS = [
  { key: 'franchise_name', label: 'Franchisee', testid: 'fo-franchise-filter' },
  { key: 'store_location', label: 'Location / Store', testid: 'fo-location-filter' },
  { key: 'order_status', label: 'Order Status', testid: 'fo-status-filter' },
  { key: 'payment_status', label: 'Payment Status', testid: 'fo-payment-filter' },
  { key: 'fulfilment_status', label: 'Fulfilment Status', testid: 'fo-fulfilment-filter' },
  { key: 'warehouse', label: 'Warehouse', testid: 'fo-warehouse-filter' },
  { key: 'order_type', label: 'Order Type', testid: 'fo-ordertype-filter' },
];

export default function AllFranchiseeOrdersReport() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [totalOrders, setTotalOrders] = useState(0);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [logRefresh, setLogRefresh] = useState(0);

  const [dateRange, setDateRange] = useState({ from: '', to: '', preset: '' });
  const [colFilters, setColFilters] = useState({});
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('order_date');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    const token = localStorage.getItem('token');
    setLoading(true);
    const params = {};
    if (dateRange.from) params.date_from = dateRange.from;
    if (dateRange.to) params.date_to = dateRange.to;
    axios
      .get(`${API}/reports/all-franchisee-orders/preview`, { params, headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        setRows(res.data?.rows || []);
        setTotalOrders(res.data?.total_orders || 0);
        setPage(1);
      })
      .catch(() => toast.error('Failed to load franchisee orders preview'))
      .finally(() => setLoading(false));
  }, [dateRange.from, dateRange.to]);

  const optionsFor = (key) => [...new Set(rows.map((r) => r[key]).filter(Boolean))].sort();

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let out = rows;
    FILTER_DEFS.forEach(({ key }) => {
      const v = colFilters[key];
      if (v && v !== 'all') out = out.filter((r) => r[key] === v);
    });
    if (q) {
      out = out.filter((r) =>
        ['order_number', 'franchise_name', 'product_name', 'sku', 'barcode', 'linked_invoice', 'linked_dc']
          .some((k) => String(r[k] || '').toLowerCase().includes(q))
      );
    }
    return out;
  }, [rows, colFilters, search]);

  const sorted = useMemo(() => {
    const col = COLUMNS.find((c) => c.key === sortBy);
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      if (col?.num) return ((Number(a[sortBy]) || 0) - (Number(b[sortBy]) || 0)) * dir;
      return String(a[sortBy] || '').localeCompare(String(b[sortBy] || '')) * dir;
    });
  }, [filtered, sortBy, sortDir]);

  const kpis = useMemo(() => {
    const perOrder = {};
    let qty = 0;
    const franchisees = new Set();
    filtered.forEach((r) => {
      qty += Number(r.ordered_qty) || 0;
      if (r.franchise_name) franchisees.add(r.franchise_name);
      if (r.order_number && !(r.order_number in perOrder)) {
        perOrder[r.order_number] = {
          value: Number(r.order_value) || 0,
          paid: Number(r.paid_amount) || 0,
          payment: r.payment_status,
          fulfilment: r.fulfilment_status,
        };
      }
    });
    const vals = Object.values(perOrder);
    const totalValue = vals.reduce((a, v) => a + v.value, 0);
    const outstanding = vals.reduce(
      (a, v) => a + (v.fulfilment === 'cancelled' || v.payment === 'paid' ? 0 : Math.max(v.value - v.paid, 0)), 0);
    return {
      orders: vals.length,
      value: totalValue,
      qty,
      franchisees: franchisees.size,
      pending: vals.filter((v) => v.fulfilment === 'pending').length,
      fulfilled: vals.filter((v) => v.fulfilment === 'fulfilled').length,
      cancelled: vals.filter((v) => v.fulfilment === 'cancelled').length,
      avgValue: vals.length ? totalValue / vals.length : 0,
      outstanding,
    };
  }, [filtered]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageRows = sorted.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const toggleSort = (key) => {
    if (sortBy === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(key); setSortDir('desc'); }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (dateRange.from) params.date_from = dateRange.from;
      if (dateRange.to) params.date_to = dateRange.to;
      const res = await axios.get(`${API}/reports/all-franchisee-orders.xlsx`, {
        params, headers: { Authorization: `Bearer ${token}` }, responseType: 'blob', timeout: 300000,
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `all_franchisee_orders_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Franchisee orders report exported');
      setLogRefresh((n) => n + 1);
    } catch (e) {
      toast.error(e?.code === 'ECONNABORTED' ? 'Report took too long — narrow the date range and try again' : 'Failed to download report');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="franchisee-orders-report-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/reports')} data-testid="fo-back-btn">
            <ArrowLeft size={16} className="mr-1" /> Back
          </Button>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ClipboardList className="text-indigo-600" size={26} /> All Franchisee Orders
          </h1>
        </div>
        <div className="flex items-center gap-2">
        <Button
          onClick={handleDownload}
          disabled={downloading || loading}
          className="bg-indigo-600 hover:bg-indigo-700 text-white gap-2"
          data-testid="fo-download-btn"
        >
          {downloading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
          {downloading ? 'Preparing…' : 'Download Excel'}
        </Button>
        <CsvDownloadButton onDownload={handleDownload} testId="fo-download-csv" size="default" disabled={loading} />
        </div>
      </div>

      {/* ── Filters ─────────────────────────────────────────── */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-56">
              <DateRangeFilter label="Order Date" from={dateRange.from} to={dateRange.to} preset={dateRange.preset}
                onChange={(v) => { setDateRange(v); setPage(1); }} testId="fo-date" />
            </div>
            {FILTER_DEFS.map(({ key, label, testid }) => (
              <div key={key} className="w-40">
                <Label className="text-xs text-slate-500">{label}</Label>
                <Select
                  value={colFilters[key] || 'all'}
                  onValueChange={(v) => { setColFilters((f) => ({ ...f, [key]: v })); setPage(1); }}
                >
                  <SelectTrigger className="h-9 mt-1" data-testid={testid}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {optionsFor(key).map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ))}
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs text-slate-500">Search</Label>
              <div className="relative mt-1">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  placeholder="Order #, franchisee, product, barcode…"
                  className="pl-9 h-9"
                  data-testid="fo-search-input"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── KPI dashboard ───────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <KPI label="Total Orders" value={loading ? '…' : kpis.orders} accent="border-indigo-200 bg-indigo-50/50" testid="kpi-fo-orders" />
        <KPI label="Total Order Value" value={loading ? '…' : `₹${fmtINR(kpis.value)}`} accent="border-violet-200 bg-violet-50/50" testid="kpi-fo-value" />
        <KPI label="Total Quantity" value={loading ? '…' : fmtQty(kpis.qty)} accent="border-sky-200 bg-sky-50/50" testid="kpi-fo-qty" />
        <KPI label="Total Franchisees" value={loading ? '…' : kpis.franchisees} accent="border-teal-200 bg-teal-50/50" testid="kpi-fo-franchisees" />
        <KPI label="Pending Orders" value={loading ? '…' : kpis.pending} accent="border-amber-200 bg-amber-50/50" testid="kpi-fo-pending" />
        <KPI label="Fulfilled Orders" value={loading ? '…' : kpis.fulfilled} accent="border-emerald-200 bg-emerald-50/50" testid="kpi-fo-fulfilled" />
        <KPI label="Cancelled Orders" value={loading ? '…' : kpis.cancelled} accent="border-rose-200 bg-rose-50/50" testid="kpi-fo-cancelled" />
        <KPI label="Average Order Value" value={loading ? '…' : `₹${fmtINR(kpis.avgValue)}`} accent="border-fuchsia-200 bg-fuchsia-50/50" testid="kpi-fo-avg" />
        <KPI label="Outstanding Amount" value={loading ? '…' : `₹${fmtINR(kpis.outstanding)}`} accent="border-orange-200 bg-orange-50/50" testid="kpi-fo-outstanding" />
      </div>

      {/* ── Preview table ───────────────────────────────────── */}
      <Card className="border-slate-200">
        <CardContent className="p-4 space-y-4">
          <div className="text-xs text-slate-500 text-right">
            Showing {pageRows.length} of {sorted.length} rows · Page {safePage}/{totalPages}
            {totalOrders ? ` · ${totalOrders} orders all-time` : ''}
          </div>
          <div className="overflow-x-auto border rounded-lg">
            <table className="min-w-full text-xs" data-testid="fo-table">
              <thead className="bg-slate-100 text-slate-600 uppercase tracking-wide">
                <tr>
                  {COLUMNS.map((c) => (
                    <th key={c.key} onClick={() => toggleSort(c.key)}
                      className={`px-3 py-2 font-semibold whitespace-nowrap cursor-pointer select-none hover:text-slate-900 ${c.num ? 'text-right' : 'text-left'}`}
                      data-testid={`fo-sort-${c.key}`}>
                      <span className="inline-flex items-center gap-1">
                        {c.label}
                        <ArrowUpDown size={11} className={sortBy === c.key ? 'text-indigo-600' : 'text-slate-400'} />
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr><td colSpan={COLUMNS.length} className="px-3 py-10 text-center text-slate-400">
                    <Loader2 size={18} className="animate-spin inline mr-2" /> Loading report…
                  </td></tr>
                ) : pageRows.length === 0 ? (
                  <tr><td colSpan={COLUMNS.length} className="px-3 py-10 text-center text-slate-400">No order rows match the current filters</td></tr>
                ) : (
                  pageRows.map((r, i) => (
                    <tr key={`${r.order_number}-${i}`} className="hover:bg-indigo-50/40" data-testid="fo-row">
                      {COLUMNS.map((c) => (
                        <td key={c.key} className={`px-3 py-2 whitespace-nowrap ${c.num ? 'text-right tabular-nums' : 'text-left'}`}>
                          {c.qty
                            ? (r[c.key] ?? 0)
                            : c.num
                              ? (r[c.key] === null || r[c.key] === undefined || r[c.key] === '' ? '—' : `₹${fmtINR(r[c.key])}`)
                              : (r[c.key] || '—')}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-end gap-2">
              <Button variant="outline" size="sm" disabled={safePage <= 1} onClick={() => setPage(safePage - 1)} data-testid="fo-prev-page">Previous</Button>
              <Button variant="outline" size="sm" disabled={safePage >= totalPages} onClick={() => setPage(safePage + 1)} data-testid="fo-next-page">Next</Button>
            </div>
          )}
        </CardContent>
      </Card>

      <ReportDownloadLog reportKey="all-franchisee-orders" refreshToken={logRefresh} />
    </div>
  );
}
